'use client';

import type { AnalyticsEvent, AnalyticsItem } from './types';

declare global {
	interface Window {
		MarkPixel: (name: string, data?: Record<string, unknown>) => void;
	}
}

const DEFAULT_CURRENCY = 'USD';
/** the cookie the Meta anonymous id lives in — client.ts mints it */
const META_ANONYMOUS_ID_COOKIE = 'wm_meta_aid';

export interface MarkPixelOptions {
	/**
	 * Whether marketing consent stands. Mark keeps the event for the journey
	 * either way and relays it to the ad platforms only when this is true —
	 * the same gate the browser pixel and the server relay sit behind.
	 */
	marketing?: boolean;
}

/**
 * Mark's browser pixel (loaded from the root layout) records page views on its
 * own for the journey, but the copy Meta receives has to carry this app's own
 * event id — the one fbq sent — so Meta dedupes the server copy against the
 * browser's. So page views are reported here too, id attached; Mark folds the
 * two reports of one view into one row.
 *
 * Every call carries three things beside the event: the event id, the
 * anonymous id the Meta pixel calls external_id, and whether marketing
 * consent stands. Everything else in the commerce funnel has to be reported
 * by the app because there is no Shopify theme for the pixel to watch.
 *
 * Every call below originates from the handler that performed the action:
 * `trackMarkPixel` is invoked from the storefront analytics fan-out, which is
 * fed by the PDP mount effect, the cart mutation success path, the checkout
 * button click and the search results page — never by a router listener.
 */
export function trackMarkPixel(event: AnalyticsEvent, options: MarkPixelOptions = {}) {
	const common = {
		eventId: event.eventId,
		externalId: readAnonymousId(),
		marketing: options.marketing !== false,
	};
	switch (event.name) {
		case 'page_view': {
			callMarkPixel('PageView', { ...common });
			return;
		}
		case 'view_item': {
			const item = event.items[0];
			if (!item) return;
			callMarkPixel('ViewContent', {
				...common,
				item: item.variantId,
				price: item.price ?? undefined,
				name: item.productTitle ?? undefined,
				value: item.price ?? event.value,
				currency: item.currency ?? event.currency ?? DEFAULT_CURRENCY,
			});
			return;
		}
		case 'view_item_list':
		case 'view_cart': {
			// Meta's pixel reports a list or the cart as ViewContent over every
			// item; the server copy has to say the same.
			const lines = itemLines(event.items);
			if (!lines) return;
			callMarkPixel('ViewContent', {
				...common,
				...lines,
				value: event.value ?? undefined,
				currency: event.currency ?? DEFAULT_CURRENCY,
			});
			return;
		}
		case 'search': {
			if (!event.searchQuery) return;
			callMarkPixel('Search', { ...common, q: event.searchQuery });
			return;
		}
		case 'add_to_cart': {
			// A bundle or kit add mutates several lines at once; Mark's payload is
			// per-item, so each changed line is reported as its own event. The
			// first keeps the browser pixel's event id; the rest are Mark's own.
			event.items.forEach((item, index) => {
				callMarkPixel('AddToCart', {
					...common,
					...(index > 0 ? { eventId: `${event.eventId}-${index}` } : {}),
					item: item.variantId,
					q: item.quantity,
					price: item.price ?? undefined,
					name: item.productTitle ?? undefined,
					value: item.price != null ? item.price * item.quantity : undefined,
					currency: item.currency ?? event.currency ?? DEFAULT_CURRENCY,
				});
			});
			return;
		}
		case 'remove_from_cart': {
			event.items.forEach((item, index) => {
				callMarkPixel('RemoveFromCart', {
					...common,
					...(index > 0 ? { eventId: `${event.eventId}-${index}` } : {}),
					item: item.variantId,
					q: item.quantity,
					price: item.price ?? undefined,
					name: item.productTitle ?? undefined,
				});
			});
			return;
		}
		case 'begin_checkout': {
			callMarkPixel('BeginCheckout', {
				...common,
				...(itemLines(event.items) ?? {}),
				value: event.value,
				currency: event.currency ?? DEFAULT_CURRENCY,
			});
			return;
		}
		default:
			return;
	}
}

/** the basket as Mark's pixel takes it: parallel comma lists, the first title */
function itemLines(items: AnalyticsItem[]) {
	const lines = items.filter((item) => item.variantId);
	if (lines.length === 0) return null;
	return {
		items: lines.map((item) => item.variantId).join(','),
		quantities: lines.map((item) => item.quantity).join(','),
		prices: lines.map((item) => (item.price != null ? String(item.price) : '')).join(','),
		name: lines[0]?.productTitle ?? undefined,
	};
}

function readAnonymousId(): string | undefined {
	if (typeof document === 'undefined') return undefined;
	const entry = document.cookie
		.split(';')
		.map((part) => part.trim())
		.find((part) => part.startsWith(`${META_ANONYMOUS_ID_COOKIE}=`));
	if (!entry) return undefined;
	try {
		return decodeURIComponent(entry.slice(META_ANONYMOUS_ID_COOKIE.length + 1)) || undefined;
	} catch {
		return undefined;
	}
}

/**
 * Mark's own page beacons fire before this app has said anything. This
 * tells the pixel which cookie the Meta anonymous id lives in and whether
 * marketing consent stands, so those beacons carry both — and so a visitor
 * who declined is kept for the journey and relayed nowhere.
 */
export function configureMarkPixel(options: { marketing: boolean }) {
	callMarkPixel('Config', {
		externalIdCookie: META_ANONYMOUS_ID_COOKIE,
		marketing: options.marketing,
	});
}

let lastContactSignature: string | null = null;

/**
 * Reported whenever the storefront learns who the visitor is — newsletter
 * signup, teaser capture, account sign-in, cart buyer identity. Deduped per
 * page load so the repeated identify calls those flows make don't fan out into
 * repeated Contact events.
 */
export function trackMarkPixelContact(identity: { email?: string | null; phone?: string | null }) {
	const email = identity.email?.trim().toLowerCase() || undefined;
	const phone = identity.phone?.trim() || undefined;
	if (!email && !phone) return;

	const signature = `${email ?? ''}|${phone ?? ''}`;
	if (signature === lastContactSignature) return;
	lastContactSignature = signature;

	callMarkPixel('Contact', { email, phone });
}

/** Lets a later re-identify resend Contact after consent was withdrawn. */
export function resetMarkPixelContact() {
	lastContactSignature = null;
}

function callMarkPixel(name: string, data?: Record<string, unknown>) {
	if (typeof window === 'undefined' || typeof window.MarkPixel !== 'function') return;

	try {
		window.MarkPixel(name, data);
	} catch {
		// Third-party pixels must never affect the storefront runtime.
	}
}
